import { Injectable } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';

/**
 * Centralized loading indicator service with reference counting.
 *
 * Multiple consumers can call {@link startLoading} independently.
 * The global spinner is shown on the first call and only hidden
 * once every consumer has called {@link stopLoading}.
 *
 * This replaces the previous pattern where both DatabaseService and
 * individual components called NgxSpinnerService.show()/hide()
 * independently, causing double-triggers and premature dismissal.
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalLoadingService {
  private activeTaskCount = 0;

  constructor(private spinner: NgxSpinnerService) {}

  /**
   * Increments the active task counter and shows the spinner
   * if this is the first active task.
   */
  startLoading(): void {
    this.activeTaskCount++;
    if (this.activeTaskCount === 1) {
      this.spinner.show();
    }
  }

  /**
   * Decrements the active task counter and hides the spinner
   * only when no more tasks are active.
   */
  stopLoading(): void {
    if (this.activeTaskCount > 0) {
      this.activeTaskCount--;
    }
    if (this.activeTaskCount === 0) {
      this.spinner.hide();
    }
  }

  /**
   * Force-clears all loading state and hides the spinner immediately.
   * Use as a safety net to recover from unbalanced start/stop calls.
   */
  forceStopLoading(): void {
    this.activeTaskCount = 0;
    this.spinner.hide();
  }

  /**
   * Returns whether any loading task is currently active.
   */
  get isLoading(): boolean {
    return this.activeTaskCount > 0;
  }
}
